<!--
  Copyright (C) 2022-2025 - Jeci SARL - https://jeci.fr

  This program is free software: you can redistribute it and/or modify
  it under the terms of the GNU Affero General Public License as
  published by the Free Software Foundation, either version 3 of the
  License, or (at your option) any later version.

  This program is distributed in the hope that it will be useful,
  but WITHOUT ANY WARRANTY; without even the implied warranty of
  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
  GNU Affero General Public License for more details.

  You should have received a copy of the GNU Affero General Public License
  along with this program.  If not, see https://www.gnu.org/licenses/agpl-3.0.html.
-->
<template>
  <div
    class="chat-message"
    :class="{
      'chat-message--own': isOwnMessage,
      'chat-message--system': isSystemMessage,
    }"
  >
    <!-- Avatar for other users' messages -->
    <div v-if="!isOwnMessage && !isSystemMessage" class="chat-message__avatar">
      <PrimeAvatar
        :label="avatarLabel"
        :style="{ backgroundColor: avatarColor }"
        shape="circle"
      />
    </div>

    <div class="chat-message__content">
      <!-- Username and timestamp header -->
      <div v-if="!isSystemMessage" class="chat-message__header">
        <span v-if="!isOwnMessage" class="chat-message__username">
          {{ message.u?.name || message.u?.username || $t("chat.unknownUser") }}
        </span>
        <span class="chat-message__time">
          {{ formattedTime }}
        </span>
      </div>

      <!-- Message body -->
      <div class="chat-message__body">
        <!-- Text message -->
        <div v-if="!hasAttachments" class="chat-message__text">
          {{ message.msg }}
        </div>

        <!-- Attachments -->
        <div v-if="hasAttachments" class="chat-message__attachments">
          <div
            v-for="attachment in message.attachments"
            :key="attachment._id || attachment.title"
            class="chat-message__attachment"
          >
            <!-- Image attachment -->
            <img
              v-if="isImageAttachment(attachment)"
              :src="getAttachmentUrl(attachment)"
              :alt="attachment.title || attachment.description"
              class="chat-message__attachment-image"
              @click="openAttachment(attachment)"
            />

            <!-- File attachment -->
            <div v-else class="chat-message__attachment-file">
              <i class="ri-attachment-line" aria-hidden="true"></i>
              <a
                :href="getAttachmentUrl(attachment)"
                target="_blank"
                rel="noopener noreferrer"
              >
                {{ attachment.title || $t("chat.attachment") }}
              </a>
              <span v-if="attachment.description" class="attachment-desc">
                {{ attachment.description }}
              </span>
            </div>
          </div>

          <!-- Message text with attachment -->
          <div v-if="message.msg" class="chat-message__text mt-2">
            {{ message.msg }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Avatar from "primevue/avatar";
import rocketChatAuthService from "@/services/RocketChatAuthService";

export default {
  name: "ChatMessage",
  components: {
    PrimeAvatar: Avatar,
  },
  props: {
    message: {
      type: Object,
      required: true,
    },
    currentUserId: {
      type: String,
      default: "",
    },
  },
  emits: ["attachment-click"],
  computed: {
    isOwnMessage() {
      return this.message.u?._id === this.currentUserId;
    },
    isSystemMessage() {
      return this.message.t !== undefined;
    },
    hasAttachments() {
      return this.message.attachments && this.message.attachments.length > 0;
    },
    avatarLabel() {
      const name = this.message.u?.name || this.message.u?.username || "?";
      return name.charAt(0).toUpperCase();
    },
    avatarColor() {
      // Generate consistent color from username
      const username = this.message.u?.username || "";
      let hash = 0;
      for (let i = 0; i < username.length; i++) {
        hash = username.charCodeAt(i) + ((hash << 5) - hash);
      }
      const hue = Math.abs(hash % 360);
      return `hsl(${hue}, 65%, 45%)`;
    },
    formattedTime() {
      if (!this.message.ts) {
        return "";
      }

      const date = new Date(this.message.ts);
      const now = new Date();
      const isToday = date.toDateString() === now.toDateString();

      if (isToday) {
        return date.toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit",
        });
      }

      return date.toLocaleString([], {
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    },
  },
  methods: {
    isImageAttachment(attachment) {
      if (attachment.image_url) {
        return true;
      }
      const imageTypes = ["image/jpeg", "image/png", "image/gif", "image/webp"];
      return imageTypes.includes(attachment.image_type);
    },
    getAttachmentUrl(attachment) {
      const baseUrl = rocketChatAuthService.getServerUrl();
      const path = attachment.image_url || attachment.title_link;

      if (!path) {
        return "";
      }

      if (path.startsWith("http")) {
        return path;
      }

      return `${baseUrl}${path}`;
    },
    openAttachment(attachment) {
      this.$emit("attachment-click", attachment);
    },
  },
};
</script>

<style lang="css" scoped>
.chat-message {
  display: flex;
  gap: 0.5rem;
  padding: 0.5rem;
  margin-bottom: 0.25rem;
}

.chat-message--own {
  flex-direction: row-reverse;
}

.chat-message--system {
  justify-content: center;
}

.chat-message__avatar {
  flex-shrink: 0;
}

.chat-message__content {
  max-width: 75%;
  min-width: 0;
}

.chat-message--own .chat-message__content {
  align-items: flex-end;
}

.chat-message__header {
  display: flex;
  gap: 0.5rem;
  align-items: baseline;
  margin-bottom: 0.25rem;
  font-size: 0.75rem;
}

.chat-message--own .chat-message__header {
  flex-direction: row-reverse;
}

.chat-message__username {
  font-weight: 600;
  color: var(--text-color);
}

.chat-message__time {
  color: var(--text-color-secondary);
}

.chat-message__body {
  background-color: var(--surface-100);
  border-radius: 0.75rem;
  padding: 0.5rem 0.75rem;
  word-wrap: break-word;
}

.chat-message--own .chat-message__body {
  background-color: var(--primary-100);
}

.chat-message--system .chat-message__body {
  background-color: transparent;
  color: var(--text-color-secondary);
  font-size: 0.85rem;
  font-style: italic;
}

.chat-message__text {
  white-space: pre-wrap;
  line-height: 1.4;
}

.chat-message__attachment-image {
  max-width: 100%;
  max-height: 300px;
  border-radius: 0.5rem;
  cursor: pointer;
}

.chat-message__attachment-file {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.chat-message__attachment-file i {
  color: var(--text-color-secondary);
}

.chat-message__attachment-file a {
  color: var(--primary-color);
  text-decoration: none;
}

.chat-message__attachment-file a:hover {
  text-decoration: underline;
}

.attachment-desc {
  color: var(--text-color-secondary);
  font-size: 0.85rem;
}
</style>
